import React from "react";

import {
  EarthLock,
  Eye,
  Gift,
  Link as LinkIcon,
  Pencil,
  Trash2,
  User,
} from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { AdvertisingEnum } from "../../../types/advertisings";
import ItemStatus from "../../../utils/ItemStatus";
import { ADVERTISING_TYPE_ICONS } from "../../advertisings/advertisingTypeIcons";
import PaymentIcon from "../../analytics/transactionAnalytics/tableParams/PaymentIcon";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { actionsColumn, statusColumn } from "../../dataTable/dataTableColumns";
import DataTableStatusDot from "../../dataTable/DataTableStatusDot";
import { CELL_ICON_CLASS } from "../../dataTable/dataTableStyles";
import { isMobilePlatform } from "../offerPlan.constants";
import { apiToPlanForm } from "../offerPlanAdapter";
import {
  getPlanDisplayedPrice,
  getPlanDisplayedRentalDuration,
  getPlanGeoblockLabel,
  getPlanStatus,
} from "../offerPlanView";

import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";
import type { JSX } from "react";

import type { PlanFormValues } from "../offerPlanAdapter";
import type { CellValue, OfferPlanRow } from "./offerPlanColumns.types";
import type { Advertising, ProviderPlan } from "../../../api/OfferAPI.types";
import type { OfferPlanTypeValue } from "../../../models/OfferPlan.types";
import type { OfferPlanType } from "../../../types";
import type { PaymentKind } from "../../analytics/transactionAnalytics/tableParams/paymentIcons.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
  FilterOption,
  StatusTone,
} from "../../dataTable/dataTable.types";

interface OfferPlanRowOptions {
  plan: OfferPlanType;
  countryLabels: string[];
  t: TFunction;
}

interface OfferPlanColumnsOptions {
  t: TFunction;
  planType: OfferPlanTypeValue;
  providerPlans: ProviderPlan[];
  onEdit: (row: OfferPlanRow) => void;
  onCopyLink: (link: string) => void;
  onDelete: (row: OfferPlanRow) => void;
  baseUrl: string | undefined;
}

// Stripe avant PayPal, quel que soit l'ordre de l'API.
const PROVIDER_ORDER_LIST: ProviderPlan[] = ["stripe", "paypal"];

function getRowPlatforms(plan: OfferPlanType, isSvod: boolean): PaymentKind[] {
  switch (plan.platform) {
    case "okast": {
      // Un abonnement sans fournisseur retombe sur Stripe.
      const providerList = plan.provider_plans ?? (isSvod ? ["stripe"] : []);
      return PROVIDER_ORDER_LIST.filter((provider) =>
        providerList.includes(provider),
      );
    }
    case "apple":
    case "google":
    case "retailer":
      return [plan.platform];
    default:
      return [];
  }
}

function getPlatformLabel(platform: PaymentKind[], t: TFunction): string {
  if (platform.includes("stripe") && platform.includes("paypal")) {
    return t("stripePaypalPlatformTooltip");
  }
  return platform.map((kind) => t(`${kind}PlatformTooltip`)).join(", ");
}

function getFrequencyLabel(plan: PlanFormValues, t: TFunction): string {
  if (isMobilePlatform(plan.platform)) return "";
  if (plan.type !== "svod") return getPlanDisplayedRentalDuration(plan);
  return plan.frequency ? t("month", { count: plan.frequency }) : "";
}

function getTooltipLines(label: string): string[] {
  return label.split("\n");
}

export function offerPlanRow({
  plan: rawPlan,
  countryLabels,
  t,
}: OfferPlanRowOptions): OfferPlanRow {
  // Les abonnés et les achats ne sont pas dans le formulaire : pris du plan brut.
  const plan = apiToPlanForm(rawPlan);
  const isSvod = plan.type === "svod";
  const isFree = plan.type === "free";
  const isOkast = plan.platform === "okast";
  const isMobile = isMobilePlatform(plan.platform);
  const platformList = getRowPlatforms(rawPlan, isSvod);

  const { subscriptions, purchases } = rawPlan;
  const geoblockMode = plan.geoblock_mode ?? "disabled";
  const isGeoblocked = geoblockMode !== "disabled";
  const hasGeoblockTarget =
    plan.countries.length + plan.country_groups.length > 0;

  const geoblockIcon = isFree
    ? isGeoblocked && hasGeoblockTarget
    : isOkast && isGeoblocked;
  const groupLabelList = plan.country_groups.map(({ label }) => label ?? "");
  const geoblockLabelList = [...groupLabelList, ...countryLabels].filter(
    Boolean,
  );

  const geoblockTooltipLineList = geoblockIcon
    ? [getPlanGeoblockLabel(plan, countryLabels)]
    : [];
  const restrictionTooltipLineList = isFree
    ? [
        isGeoblocked && geoblockLabelList.length > 0
          ? `${t(`common:${geoblockMode}_countries`)} ${geoblockLabelList.join(", ")}`
          : "",
        plan.require_registration ? t("requireRegistration") : "",
      ].filter(Boolean)
    : geoblockTooltipLineList;

  const trialDuration = plan.trial_duration ?? 0;
  const subscribersActive = subscriptions?.ongoing_paid ?? 0;
  const purchasesActive = purchases?.ongoing_paid ?? 0;

  return {
    uuid: plan.uuid,
    type: plan.type,
    status: getPlanStatus(plan),
    platform: platformList,
    platform_label: getPlatformLabel(platformList, t),
    frequency: isSvod && !isMobile ? (plan.frequency ?? null) : null,
    frequency_label: getFrequencyLabel(plan, t),
    trial_duration: trialDuration,
    trial_label:
      trialDuration > 0
        ? t("trial_period_value", { count: trialDuration })
        : "",
    // Prix brut de l'API : le prix normalisé porte le minimum du formulaire.
    price: isOkast ? (rawPlan.price ?? null) : null,
    price_label: isOkast ? getPlanDisplayedPrice(rawPlan) : "",
    resolution: plan.resolution,
    geoblock_icon: geoblockIcon,
    registration_icon: isFree && plan.require_registration,
    restrictions_tooltip: restrictionTooltipLineList,
    subscribers_count: subscribersActive,
    subscribers_tooltip: getTooltipLines(
      t("users", {
        active: subscribersActive,
        total: subscriptions?.total ?? 0,
      }),
    ),
    active_purchases: purchasesActive,
    active_purchases_tooltip:
      (purchases?.total ?? 0) > 0
        ? getTooltipLines(
            t("users", {
              active: purchasesActive,
              total: purchases?.total ?? 0,
            }),
          )
        : [],
    free_copies:
      (isSvod
        ? subscriptions?.ongoing_freecopies
        : purchases?.ongoing_freecopies) ?? 0,
    donation_enabled: plan.donation_enabled ?? false,
    platform_product_id: plan.platform_product_id ?? "",
    advertising: plan.advertising ?? null,
    freemium: plan.freemium ?? false,
    has_links: isOkast && plan.enabled && !isFree,
    can_delete:
      !plan.enabled &&
      (subscriptions?.total ?? 0) === 0 &&
      (purchases?.total ?? 0) === 0,
  };
}

interface ValueColumnOptions {
  id: string;
  header: string;
  layout: ColumnLayout;
  isSortable?: boolean;
  filterOptions?: FilterOption[];
  getValue: (row: OfferPlanRow) => CellValue;
  cell: (row: OfferPlanRow) => JSX.Element | string | null;
}

function valueColumn({
  id,
  header,
  layout,
  isSortable = false,
  filterOptions,
  getValue,
  cell,
}: ValueColumnOptions): DataTableColumnDef<OfferPlanRow> {
  return {
    id,
    accessorFn: getValue,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    enableSorting: isSortable,
    meta: { ...layout, label: header, filterOptions },
    cell: ({ row }) => cell(row.original),
  };
}

interface TooltipCellProps {
  lines: string[];
  children: JSX.Element | string;
}

function TooltipCell({ lines, children }: TooltipCellProps): JSX.Element {
  if (lines.length === 0) return <React.Fragment>{children}</React.Fragment>;

  return (
    <span
      className="inline-flex items-center gap-1 align-middle"
      data-tooltip-html={renderToStaticMarkup(<TooltipLines lines={lines} />)}
      data-tooltip-id={APP_TOOLTIP_ID}
    >
      {children}
      <span className="sr-only">{lines.join(" ")}</span>
    </span>
  );
}

interface PlanRowCellProps {
  row: OfferPlanRow;
}

function PlatformCell({ row }: PlanRowCellProps): JSX.Element | null {
  if (row.platform.length === 0) return null;

  return (
    <span
      className="inline-flex items-center gap-1 align-middle"
      data-tooltip-content={row.platform_label}
      data-tooltip-id={APP_TOOLTIP_ID}
    >
      {row.platform.map((kind) => (
        <PaymentIcon key={kind} kind={kind} />
      ))}
      <span className="sr-only">{row.platform_label}</span>
    </span>
  );
}

function RestrictionsCell({ row }: PlanRowCellProps): JSX.Element | null {
  if (!row.geoblock_icon && !row.registration_icon) return null;

  return (
    <TooltipCell lines={row.restrictions_tooltip}>
      <React.Fragment>
        {row.geoblock_icon ? (
          <EarthLock aria-hidden="true" className={CELL_ICON_CLASS} />
        ) : null}
        {row.registration_icon ? (
          <User aria-hidden="true" className={CELL_ICON_CLASS} />
        ) : null}
      </React.Fragment>
    </TooltipCell>
  );
}

const ADVERTISING_TYPE_LABEL_KEYS: Record<AdvertisingEnum, string> = {
  [AdvertisingEnum.CSAI_MP4]: "editor-advertisings:CSAIMP4",
  [AdvertisingEnum.CSAI_VAST]: "editor-advertisings:CSAIVast",
};

const ADVERTISING_STATUS_TONES: Record<string, StatusTone> = {
  enabled: "success",
  paused: "warning",
  disabled: "error",
};

interface AdvertisingTooltipProps {
  advertising: Advertising;
  TypeIcon: LucideIcon;
  typeLabel: string;
}

function AdvertisingTooltip({
  advertising,
  TypeIcon,
  typeLabel,
}: AdvertisingTooltipProps): JSX.Element {
  const status = String(ItemStatus.getAdvertisingStatus(advertising));

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <TypeIcon aria-hidden="true" className="size-4" />
        {typeLabel}
      </div>
      <div className="flex items-center gap-1.5">
        <DataTableStatusDot
          tone={ADVERTISING_STATUS_TONES[status] ?? "neutral"}
        />
        {advertising.label}
      </div>
    </div>
  );
}

interface AdvertisingCellProps {
  advertising: Advertising | null;
  t: TFunction;
}

function AdvertisingCell({
  advertising,
  t,
}: AdvertisingCellProps): JSX.Element | string | null {
  if (!advertising) return null;

  const TypeIcon: LucideIcon | undefined =
    ADVERTISING_TYPE_ICONS[advertising.type];
  if (!TypeIcon) return advertising.label;

  const typeLabel = t(ADVERTISING_TYPE_LABEL_KEYS[advertising.type]);
  const status = t(
    `table:status.${String(ItemStatus.getAdvertisingStatus(advertising))}`,
  );

  // React échappe le libellé de la campagne (valeur de l'API).
  const tooltipHtml = renderToStaticMarkup(
    <AdvertisingTooltip
      advertising={advertising}
      TypeIcon={TypeIcon}
      typeLabel={typeLabel}
    />,
  );

  return (
    <span
      className="inline-flex items-center align-middle"
      data-tooltip-html={tooltipHtml}
      data-tooltip-id={APP_TOOLTIP_ID}
    >
      <TypeIcon aria-hidden="true" className={CELL_ICON_CLASS} />
      <span className="sr-only">
        {[typeLabel, advertising.label, status].join(" ")}
      </span>
    </span>
  );
}

function getRowLabel(row: OfferPlanRow, t: TFunction): string {
  return [t(`pricing_${row.type}`), row.platform_label, row.frequency_label]
    .filter(Boolean)
    .join(" - ");
}

export function offerPlanColumns({
  t,
  planType,
  providerPlans,
  onEdit,
  onCopyLink,
  onDelete,
  baseUrl,
}: OfferPlanColumnsOptions): DataTableColumnDef<OfferPlanRow>[] {
  const label = (key: string): string => t(key);
  const yesNo = (value: boolean): string => label(value ? "yes" : "no");

  const statusCol = statusColumn<OfferPlanRow>("status", label("status"), [
    { value: "enabled", label: label("table:status.enabled"), tone: "success" },
    { value: "disabled", label: label("table:status.disabled"), tone: "error" },
  ]);

  const platformKindList: PaymentKind[] = ["stripe"];
  if (planType === "tvod_bundle" && providerPlans.includes("paypal")) {
    platformKindList.push("paypal");
  }
  platformKindList.push("apple", "google", "retailer");

  const platformFilterOptionList: FilterOption[] = platformKindList.map(
    (kind) => ({
      value: kind,
      label: label(`${kind}PlatformTooltip`),
      icon: <PaymentIcon kind={kind} />,
    }),
  );

  const platformCol = (width: `${number}%`) =>
    valueColumn({
      id: "platform",
      header: label("platform"),
      layout: { width, minWidth: 85, align: "center" },
      filterOptions: platformFilterOptionList,
      getValue: (row) => row.platform,
      cell: (row) => <PlatformCell row={row} />,
    });

  const resolutionCol = (width: `${number}%`) =>
    valueColumn({
      id: "resolution",
      header: label("resolution"),
      layout: { width, minWidth: 110, align: "center" },
      isSortable: true,
      getValue: (row) => row.resolution,
      cell: (row) => row.resolution.toUpperCase(),
    });

  // Le compte requis n'existe que sur la gratuité.
  const restrictionFilterOptionList: FilterOption[] = [
    {
      value: "geoblock_mode",
      label: label("editor-content:restrictionGeoblock"),
      icon: <EarthLock />,
    },
    ...(planType === "free"
      ? [
          {
            value: "require_registration",
            label: label("requireRegistration"),
            icon: <User />,
          },
        ]
      : []),
  ];

  const restrictionsCol = (width: `${number}%`) =>
    valueColumn({
      id: "restrictions",
      header: label("restrictions"),
      layout: { width, minWidth: 100, align: "center" },
      filterOptions: restrictionFilterOptionList,
      getValue: (row) => [
        ...(row.geoblock_icon ? ["geoblock_mode"] : []),
        ...(row.registration_icon ? ["require_registration"] : []),
      ],
      cell: (row) => <RestrictionsCell row={row} />,
    });

  const donationCol = (width: `${number}%`) =>
    valueColumn({
      id: "donation_enabled",
      header: label("donation_enabled"),
      layout: { width, minWidth: 95, align: "center" },
      isSortable: true,
      getValue: (row) => row.donation_enabled,
      cell: (row) => yesNo(row.donation_enabled),
    });

  const freeCopiesCol = (width: `${number}%`) =>
    valueColumn({
      id: "free_copies",
      header: label("free_copies"),
      layout: { width, minWidth: 150, align: "center" },
      isSortable: true,
      getValue: (row) => row.free_copies,
      cell: (row) => String(row.free_copies),
    });

  const skuCol = (width: `${number}%`) =>
    valueColumn({
      id: "platform_product_id",
      header: label("platform_product_id"),
      layout: { width, minWidth: 85, align: "center" },
      isSortable: true,
      getValue: (row) => row.platform_product_id,
      cell: (row) =>
        row.platform_product_id ? (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={row.platform_product_id}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <Eye aria-hidden="true" className={CELL_ICON_CLASS} />
            <span className="sr-only">{row.platform_product_id}</span>
          </span>
        ) : null,
    });

  const advertisingCol = (width: `${number}%`) =>
    valueColumn({
      id: "advertising_label",
      header: label("advertising"),
      layout: { width, minWidth: 80, align: "center" },
      getValue: (row) => row.advertising?.label ?? null,
      cell: (row) => <AdvertisingCell advertising={row.advertising} t={t} />,
    });

  const priceCol = (width: `${number}%`) =>
    valueColumn({
      id: "price",
      header: label("price"),
      layout: { width, minWidth: 80 },
      isSortable: true,
      getValue: (row) => row.price,
      cell: (row) => row.price_label,
    });

  const frequencyCol = (
    width: `${number}%`,
  ): DataTableColumnDef<OfferPlanRow> =>
    planType === "svod"
      ? valueColumn({
          id: "frequency",
          header: label("frequency_svod"),
          layout: { width, minWidth: 112 },
          isSortable: true,
          getValue: (row) => row.frequency,
          cell: (row) => row.frequency_label,
        })
      : valueColumn({
          id: "frequency",
          header: label("frequency_tvod_bundle"),
          layout: { width, minWidth: 120 },
          getValue: (row) => row.frequency_label,
          cell: (row) => row.frequency_label,
        });

  const trialCol = valueColumn({
    id: "trial_duration",
    header: label("trial_duration_short"),
    layout: { width: "6%", minWidth: 75 },
    isSortable: true,
    getValue: (row) => row.trial_duration,
    cell: (row) => row.trial_label,
  });

  const subscribersCol = valueColumn({
    id: "subscribers_count",
    header: label("subscribers_count"),
    layout: { width: "9%", minWidth: 105, align: "center" },
    isSortable: true,
    getValue: (row) => row.subscribers_count,
    cell: (row) => (
      <TooltipCell lines={row.subscribers_tooltip}>
        {String(row.subscribers_count)}
      </TooltipCell>
    ),
  });

  const activePurchasesCol = valueColumn({
    id: "active_purchases",
    header: label("active_purchases"),
    layout: { width: "11%", minWidth: 130, align: "center" },
    isSortable: true,
    getValue: (row) => row.active_purchases,
    cell: (row) => (
      <TooltipCell lines={row.active_purchases_tooltip}>
        {String(row.active_purchases)}
      </TooltipCell>
    ),
  });

  const freemiumCol = valueColumn({
    id: "freemium",
    header: label("freemium"),
    layout: { width: "14%", minWidth: 110, align: "center" },
    isSortable: true,
    getValue: (row) => row.freemium,
    cell: (row) => yesNo(row.freemium),
  });

  const actionsCol = actionsColumn<OfferPlanRow>(
    (row) => [
      {
        label: label("edit"),
        icon: <Pencil />,
        onClick: () => onEdit(row),
      },
      {
        label: label("offerLink"),
        icon: <LinkIcon />,
        hidden: !row.has_links,
        onClick: () => onCopyLink(`${baseUrl}/offers/plans/${row.uuid}`),
      },
      {
        label: label("giftLink"),
        icon: <Gift />,
        hidden: !row.has_links,
        onClick: () => onCopyLink(`${baseUrl}/offers/gift_plan/${row.uuid}`),
      },
      {
        label: label("delete"),
        icon: <Trash2 />,
        variant: "destructive",
        hidden: !row.can_delete,
        onClick: () => onDelete(row),
        confirm: {
          title: label("deleteTitle"),
          description: label("deleteBody"),
          confirmLabel: label("deleteConfirm"),
        },
      },
    ],
    (row) => getRowLabel(row, t),
  );

  switch (planType) {
    case "svod":
      return [
        statusCol,
        platformCol("7%"),
        frequencyCol("9%"),
        { ...trialCol, enableHiding: true },
        priceCol("7%"),
        { ...resolutionCol("9%"), enableHiding: true },
        { ...restrictionsCol("8%"), enableHiding: true },
        { ...subscribersCol, enableHiding: true },
        { ...donationCol("8%"), enableHiding: true },
        { ...freeCopiesCol("12%"), enableHiding: true },
        { ...skuCol("7%"), enableHiding: true },
        { ...advertisingCol("7%"), enableHiding: true },
        actionsCol,
      ];
    case "tvod_bundle":
      return [
        statusCol,
        platformCol("8%"),
        frequencyCol("10%"),
        priceCol("7%"),
        { ...resolutionCol("9%"), enableHiding: true },
        { ...restrictionsCol("8%"), enableHiding: true },
        activePurchasesCol,
        { ...donationCol("8%"), enableHiding: true },
        { ...freeCopiesCol("12%"), enableHiding: true },
        { ...skuCol("8%"), enableHiding: true },
        { ...advertisingCol("7%"), enableHiding: true },
        actionsCol,
      ];
    default:
      return [
        statusCol,
        frequencyCol("20%"),
        resolutionCol("20%"),
        restrictionsCol("20%"),
        advertisingCol("11%"),
        freemiumCol,
        actionsCol,
      ];
  }
}
